import { Component, Event, EventEmitter, Host, Prop, h } from '@stencil/core';
import '@material/web/select/select-option';
import { type MdSelectOption } from '@material/web/select/select-option';

/** The design system equivalent of `<option>` within a `<select>`.
 * @slot - Text to display to the user for the option in the list. This is also used as a default value for displayText and typeaheadText.
 * @slot leading-icon - Space for an icon in front of the text displayed for the option.
 */
@Component({
  tag: 'omk-select-option',
  styleUrl: 'omk-select-option.css',
  shadow: { delegatesFocus: true },
})
export class OmkSelectOption {
  /** The value to submit with the form on selection. */
  @Prop() value: string = '';
  @Prop({ mutable: true }) selected: boolean;
  /** Text to display when selected. Defaults to the slotted text. */
  @Prop({ mutable: true }) displayText: string;
  /** Defaults to match displayText. Defaults to the slotted text. */
  @Prop({ mutable: true }) typeaheadText: string;
  /** Disable selection of this option. This is useful for deprecating saved options that are no longer valid. */
  @Prop({ reflect: true }) disabled: boolean;
  /** @internal Allows the containing omk-select to correctly set its value on load. */
  @Event({ eventName: 'omk-select-option-initial-value', composed: false, bubbles: true, cancelable: false })
  initialValueEmitter: EventEmitter;

  customDisplayText: boolean;
  customTypeaheadText: boolean;

  componentWillRender() {
    if (this.displayText) {
      this.customDisplayText = true;
    }
    if (this.typeaheadText) {
      this.customDisplayText = true;
    }
  }

  componentWillLoad() {
    if (this.selected) {
      this.initialValueEmitter.emit(this.value);
    }
  }

  handleMainTextSlotUpdate(event) {
    const slottedText =
      (event.target as HTMLSlotElement)
        .assignedNodes()
        .map(node => node.textContent)
        .join('')
        .trim() ?? '';
    if (!this.customDisplayText) {
      this.displayText = slottedText;
    }
    if (!this.customTypeaheadText) {
      this.typeaheadText = slottedText;
    }
  }

  innerMaterialOption: MdSelectOption;

  render() {
    return (
      <Host md-menu-item>
        <md-select-option
          value={this.value}
          selected={this.selected}
          disabled={this.disabled}
          ref={el => (this.innerMaterialOption = el)}
        >
          <slot name="leading-icon" slot="start"></slot>
          <div class="primary-text-wrapper">
            <slot onSlotchange={e => this.handleMainTextSlotUpdate(e)} />
          </div>
          {this.selected ? <omk-icon slot="end">check</omk-icon> : ''}
        </md-select-option>
      </Host>
    );
  }
}
